import { useState } from 'react';

interface KeyValuePair {
  key: string;
  value: string;
}

interface UseKeyValuePairsReturn {
  pairs: KeyValuePair[];
  keyErrors: Record<number, string>;
  valueErrors: Record<number, string>;
  add: () => void;
  remove: (index: number) => void;
  update: (index: number, field: 'key' | 'value', value: string) => void;
  setPairs: React.Dispatch<React.SetStateAction<KeyValuePair[]>>;
  setKeyErrors: React.Dispatch<React.SetStateAction<Record<number, string>>>;
  setValueErrors: React.Dispatch<React.SetStateAction<Record<number, string>>>;
}

export const useKeyValuePairs = (initialData?: Record<string, string>): UseKeyValuePairsReturn => {
  const [pairs, setPairs] = useState<KeyValuePair[]>(
    initialData ? Object.entries(initialData).map(([key, value]) => ({ key, value })) : [],
  );
  const [keyErrors, setKeyErrors] = useState<Record<number, string>>({});
  const [valueErrors, setValueErrors] = useState<Record<number, string>>({});

  const add = () => {
    setPairs([...pairs, { key: '', value: '' }]);
  };

  const remove = (index: number) => {
    setPairs(pairs.filter((_, i) => i !== index));
    if (keyErrors[index]) {
      const newKeyErrors = { ...keyErrors };
      delete newKeyErrors[index];
      setKeyErrors(newKeyErrors);
    }
    if (valueErrors[index]) {
      const newValueErrors = { ...valueErrors };
      delete newValueErrors[index];
      setValueErrors(newValueErrors);
    }
  };

  const update = (index: number, field: 'key' | 'value', value: string) => {
    const updated = [...pairs];
    updated[index][field] = value;
    setPairs(updated);

    if (field === 'key' && keyErrors[index]) {
      const newKeyErrors = { ...keyErrors };
      delete newKeyErrors[index];
      setKeyErrors(newKeyErrors);
    }
    if (field === 'value' && valueErrors[index]) {
      const newValueErrors = { ...valueErrors };
      delete newValueErrors[index];
      setValueErrors(newValueErrors);
    }
  };

  return {
    pairs,
    keyErrors,
    valueErrors,
    add,
    remove,
    update,
    setPairs,
    setKeyErrors,
    setValueErrors,
  };
};
